import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Pressable } from "@-/com.npmjs.react-native";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@-/dev.improve.haniverse.components.ui.alert-dialog";
import { useAuthSheet } from "@-/dev.improve.haniverse.routes.nav.app.auth.sheet";
import { useSlideLessonContinueMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._lib.continue";
import { useState } from "react";

/**
 * Footer buttons authored inside `<SlideFooter>` on account-prompt slides.
 *
 * The slide footer is a FLOATING lane (see `slide-lesson-page-content`), not a chrome bar, so
 * these match the default continue affordance: `rounded-full` pills that hug their label and
 * sit centred above the slide content, rather than full-width bars pinned to the screen edge.
 *
 * `SignInButton` / `CreateAccountButton` both open the shared account sheet (`useAuthSheet`),
 * the same sheet the welcome deck and the profile entry points use, in sign-in and sign-up mode
 * respectively. "Create an account" is the primary action, "Sign in" the secondary one. `ContinueWithoutAccountButton` is the de-emphasized escape hatch: an
 * underlined link that confirms before leaving the slide without creating an account.
 */
export function CreateAccountButton() {
  const { openAccount } = useAuthSheet();
  return (
    <Button
      accessibilityLabel="Create an account"
      className="min-w-40 rounded-full px-10 py-3.5 shadow-lg"
      onPress={() => openAccount({ mode: "signUp", source: "slide_footer" })}
      variant="default"
    >
      <Text colorVariant="inverse">Create an account</Text>
    </Button>
  );
}

export function SignInButton() {
  const { openAccount } = useAuthSheet();
  return (
    <Button
      accessibilityLabel="Sign in"
      className="min-w-40 rounded-full px-10 py-3.5 shadow-lg"
      onPress={() => openAccount({ mode: "signIn", source: "slide_footer" })}
      variant="secondary"
    >
      <Text colorVariant="inverse">Sign in</Text>
    </Button>
  );
}

export function ContinueWithoutAccountButton() {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const continueMutation = useSlideLessonContinueMutation();

  return (
    <AlertDialog onOpenChange={setIsConfirmOpen} open={isConfirmOpen}>
      {/* A plain `Pressable` (not the `Button` component) on purpose: every `Button` variant
          bakes in a press highlight (`active:bg-accent`, an underline, etc.), and this affordance
          must have NONE of that. It's deliberately understated — tiny, very-gray subtitle text
          with no underline, no bold, no vertical padding, and no active highlight — so it reads
          as fine print the user might not even register as tappable. We want to gently
          discourage skipping account creation, not advertise it. */}
      <Pressable
        accessibilityLabel="Continue without an account"
        accessibilityRole="button"
        className="self-center"
        onPress={() => {
          setIsConfirmOpen(true);
        }}
      >
        <Text className="text-xs font-normal text-muted-foreground/60">
          Continue without an account
        </Text>
      </Pressable>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Continue without an account?</AlertDialogTitle>
          <AlertDialogDescription>
            You can keep using Haniverse without an account, but your progress won&apos;t be saved
            or synced across devices. You can always create an account later from the profile menu.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <Text>Cancel</Text>
          </AlertDialogCancel>
          <AlertDialogAction
            onPress={() => {
              setIsConfirmOpen(false);
              continueMutation.mutate();
            }}
          >
            <Text>Continue</Text>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
